import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';

import { ManagerProgressionTaskState } from '@sorare/core/src/__generated__/globalTypes';
import LoadingButton from '@sorare/core/src/atoms/buttons/LoadingButton';
import { Challenge } from '@sorare/core/src/components/challenges/Challenge';
import { glossary } from '@sorare/core/src/lib/glossary';

import { useClaimMissionTask } from 'components/rivals/MissionsPage/DailyChallenges/useClaimMissionTask';
import { RivalsRewardPreview } from 'components/rivals/RivalsRewardPreview';

import { DailyChallenge_managerProgressionMission } from './__generated__/index.graphql';

type Props = {
  task: DailyChallenge_managerProgressionMission;
  disableClaim?: boolean;
};
export const DailyChallenge = ({ task, ...rest }: Props) => {
  const [claimTask, { loading }] = useClaimMissionTask();

  const {
    title,
    description,
    progress,
    target,
    name,
    aasmState,
    configRewards,
  } = task;
  const isCompleted = aasmState === ManagerProgressionTaskState.CLAIMED;
  const isClaimable = aasmState === ManagerProgressionTaskState.COMPLETED;
  const reward = configRewards.at(0);

  const onClaim = () => {
    claimTask({ variables: { name } });
  };

  return (
    <Challenge
      progression={progress}
      target={target > 1 ? target : undefined}
      isCompleted={isCompleted || isClaimable}
      title={title}
      description={description}
      rewardPreview={
        reward ? <RivalsRewardPreview reward={reward} showDescription /> : null
      }
      claimButton={
        <LoadingButton
          size="small"
          color={isClaimable ? 'secondary' : 'tertiary'}
          disabled={!isClaimable}
          onClick={onClaim}
          loading={loading}
        >
          {task.aasmState === ManagerProgressionTaskState.CLAIMED ? (
            <FormattedMessage {...glossary.claimed} />
          ) : (
            <FormattedMessage {...glossary.claim} />
          )}
        </LoadingButton>
      }
      {...rest}
    />
  );
};

DailyChallenge.fragments = {
  managerProgressionMission: gql`
    fragment DailyChallenge_managerProgressionMission on ManagerProgressionMission {
      id
      aasmState
      description
      cutoffAt
      progress
      target
      name
      title
      configRewards {
        ...RivalsRewardPreview_managerTaskRewardInterface
      }
    }
    ${RivalsRewardPreview.fragments.managerTaskRewardInterface}
  ` as TypedDocumentNode<DailyChallenge_managerProgressionMission>,
};
